import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import * as engineService from "@/shared/api/services/engineService";
import { auth } from "@/shared/api/graphqlClient";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { exportToCsv } from "@/shared/utils/csvExport";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar as Cal } from "@/shared/components/ui/calendar";
import {
  Search, Calendar, ChevronLeft, ChevronRight, X, ListFilter,
  Activity, ArrowDownToLine, ArrowUpFromLine, Copy, Check,
  CheckCircle2, XCircle, Clock, Loader2 as Loader, Download, Webhook, RefreshCw
} from "lucide-react";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&family=Syne:wght@700;800&display=swap');

.etx {
  font-family: 'DM Sans', sans-serif;
  padding-bottom: 60px;
  --cyan: #22d3ee;
  --emerald: #10b981;
  --rose: #fb7185;
  --amber: #f59e0b;
  --blue: #3b82f6;
  --surface: rgba(8,16,38,0.96);
  --surface-2: rgba(11,22,50,0.95);
  --border: rgba(34,211,238,0.1);
  --text-1: #e8f4ff;
  --text-2: #7aaec8;
  --text-3: #3a5a78;
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}
.etx * { box-sizing: border-box; }

/* ── Header ── */
.etx-h1 { font-family: 'DM Sans', sans-serif; font-size: 1.85rem; font-weight: 600; letter-spacing: -0.015em; color: #eaf6ff; }
.etx-sub { color: #eaf6ff; font-family: 'DM Sans', sans-serif; font-size: 13px; margin-top: 6px; font-weight: 500; }

/* ── Badges ── */
.etx-summary { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 2px; }
.etx-badge { display: flex; align-items: center; gap: 7px; background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.12); border-radius: 10px; padding: 7px 14px; font-size: 12px; font-weight: 600; color: var(--text-3); cursor: default; transition: all 0.25s var(--spring); }
.etx-badge:hover { border-color: rgba(34,211,238,0.28); color: var(--text-2); transform: translateY(-1px); box-shadow: 0 6px 18px rgba(34,211,238,0.08); }
.etx-badge-dot { width: 6px; height: 6px; border-radius: 50%; }

/* ── Filter bar ── */
.etx-bar { background: linear-gradient(145deg, var(--surface), var(--surface-2)); border: 1px solid var(--border); border-radius: 20px; padding: 16px 20px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; position: relative; overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
.etx-bar::before { content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent); }
.etx-bar:focus-within { border-color: rgba(34,211,238,0.2); box-shadow: 0 4px 36px rgba(34,211,238,0.07); }

/* ── Search ── */
.etx-s-wrap { position: relative; flex: 1; min-width: 200px; }
.etx-s-ico { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); width: 15px; height: 15px; color: var(--text-3); pointer-events: none; }
.etx-s-in { width: 100%; background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1); border-radius: 12px; padding: 10px 14px 10px 40px; color: var(--text-1); font-family: 'DM Sans', sans-serif; font-size: 13.5px; outline: none; transition: border-color 0.25s, box-shadow 0.25s, background 0.25s; }
.etx-s-in::placeholder { color: var(--text-3); }
.etx-s-in:focus { border-color: rgba(34,211,238,0.38); background: rgba(34,211,238,0.07); box-shadow: 0 0 0 4px rgba(34,211,238,0.08), 0 6px 24px rgba(34,211,238,0.08); }

/* ── Selects ── */
.etx-sel { background: rgba(34,211,238,0.04) !important; border: 1px solid rgba(34,211,238,0.1) !important; color: var(--text-1) !important; border-radius: 12px !important; font-family: 'DM Sans', sans-serif !important; font-size: 13px !important; min-width: 130px; transition: border-color 0.22s, box-shadow 0.22s, transform 0.3s var(--spring) !important; will-change: transform; }
.etx-sel:hover { border-color: rgba(34,211,238,0.28) !important; background: rgba(34,211,238,0.08) !important; box-shadow: 0 4px 16px rgba(34,211,238,0.1) !important; transform: translateY(-1px) !important; }

/* ── Date button ── */
.etx-date { display: inline-flex; align-items: center; gap: 8px; background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1); border-radius: 12px; padding: 10px 14px; color: var(--text-3); font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all 0.25s var(--spring); }
.etx-date:hover { background: rgba(34,211,238,0.09); border-color: rgba(34,211,238,0.3); color: var(--cyan); box-shadow: 0 4px 18px rgba(34,211,238,0.1); transform: translateY(-2px); }
.etx-date svg { width: 14px; height: 14px; }

/* ── Clear btn ── */
.etx-clr { display: inline-flex; align-items: center; gap: 6px; background: rgba(244,63,94,0.06); border: 1px solid rgba(244,63,94,0.18); border-radius: 12px; padding: 10px 14px; color: #fca5a5; font-family: 'DM Sans', sans-serif; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: all 0.22s ease; }
.etx-clr:hover { background: rgba(244,63,94,0.12); border-color: rgba(251,113,133,0.38); transform: scale(1.03); }
.etx-clr svg { width: 13px; height: 13px; }

/* ── Export btn ── */
.etx-export { display: inline-flex; align-items: center; gap: 6px; background: rgba(34,211,238,0.06); border: 1px solid rgba(34,211,238,0.15); border-radius: 12px; padding: 9px 16px; color: var(--cyan); font-family: 'DM Sans', sans-serif; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: all 0.22s ease; white-space: nowrap; }
.etx-export:hover { background: rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.35); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(34,211,238,0.15); }
.etx-export svg { width: 14px; height: 14px; }

/* ── Table ── */
.etx-table-wrap { background: linear-gradient(145deg, var(--surface), var(--surface-2)); border: 1px solid var(--border); border-radius: 20px; overflow: hidden; position: relative; }
.etx-table-wrap::before { content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent); }
.etx-table { width: 100%; border-collapse: collapse; }
.etx-th { padding: 13px 16px; text-align: left; font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; color: rgba(34,211,238,0.4); text-transform: uppercase; letter-spacing: 0.18em; border-bottom: 1px solid rgba(34,211,238,0.07); white-space: nowrap; }
.etx-th:last-child { text-align: right; }
.etx-tr { border-bottom: 1px solid rgba(34,211,238,0.04); cursor: pointer; transition: background 0.18s; }
.etx-tr:last-child { border-bottom: none; }
.etx-tr:hover { background: rgba(34,211,238,0.04); }
.etx-td { padding: 14px 16px; vertical-align: middle; }
.etx-td:last-child { text-align: right; }

/* ── Type icon ── */
.etx-icon { width: 36px; height: 36px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.etx-icon svg { width: 16px; height: 16px; }

/* ── Status badge ── */
.etx-status { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; }

/* ── Mono text ── */
.etx-mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; }
.etx-copy { background: none; border: none; cursor: pointer; padding: 3px; border-radius: 5px; color: var(--text-3); transition: color 0.15s, background 0.15s; }
.etx-copy:hover { color: var(--cyan); background: rgba(34,211,238,0.1); }

/* ── Empty / Loading ── */
.etx-empty { text-align: center; padding: 60px 20px; color: var(--text-3); font-size: 14px; }

/* ── Pagination ── */
.etx-pag { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.etx-pag-info { color: var(--text-3); font-size: 12.5px; font-weight: 500; }
.etx-arr { width: 34px; height: 34px; border-radius: 10px; background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1); display: flex; align-items: center; justify-content: center; color: var(--text-3); cursor: pointer; transition: all 0.25s var(--spring); }
.etx-arr:hover:not(:disabled) { background: rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.32); color: var(--cyan); box-shadow: 0 4px 14px rgba(34,211,238,0.15); transform: scale(1.1); }
.etx-arr:disabled { opacity: 0.25; cursor: not-allowed; }
.etx-arr svg { width: 15px; height: 15px; }
.etx-pg { min-width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 12.5px; font-weight: 700; cursor: pointer; border: 1px solid transparent; color: var(--text-3); transition: all 0.2s var(--spring); }
.etx-pg:hover { background: rgba(34,211,238,0.09); color: var(--cyan); transform: scale(1.1); }
.etx-pg.on { background: linear-gradient(135deg, rgba(14,165,233,0.2), rgba(34,211,238,0.1)); border-color: rgba(34,211,238,0.3); color: var(--cyan); box-shadow: 0 4px 14px rgba(34,211,238,0.18); }

/* ── Detail Panel ── */
.etx-panel-ov { position: fixed; inset: 0; background: rgba(2,5,12,0.85); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); z-index: 100; }
.etx-panel { position: fixed; top: 0; right: 0; bottom: 0; width: 420px; max-width: 100vw; background: linear-gradient(180deg, #04091a 0%, #030710 100%); border-left: 1px solid rgba(34,211,238,0.12); z-index: 101; overflow-y: auto; display: flex; flex-direction: column; }
.etx-panel-head { padding: 24px 24px 20px; border-bottom: 1px solid rgba(34,211,238,0.07); flex-shrink: 0; }
.etx-panel-close { width: 32px; height: 32px; border-radius: 9px; background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.12); display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-3); transition: all 0.2s; }
.etx-panel-close:hover { background: rgba(251,113,133,0.1); border-color: rgba(251,113,133,0.25); color: var(--rose); }
.etx-panel-body { padding: 20px 24px 40px; flex: 1; }
.etx-detail-row { display: flex; align-items: flex-start; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid rgba(34,211,238,0.05); gap: 12px; }
.etx-detail-row:last-child { border-bottom: none; }
.etx-detail-lbl { font-size: 11.5px; font-weight: 600; color: var(--text-3); font-family: 'JetBrains Mono', monospace; text-transform: uppercase; letter-spacing: 0.08em; flex-shrink: 0; }
.etx-detail-val { font-size: 13px; font-weight: 500; color: var(--text-2); text-align: right; word-break: break-all; }
.etx-amount-big { font-family: 'Syne', sans-serif; font-size: 2.2rem; font-weight: 800; letter-spacing: -0.02em; }
.etx-timeline-item { display: flex; gap: 12px; align-items: flex-start; }
.etx-timeline-dot { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.etx-timeline-line { width: 2px; height: 28px; margin: 2px 0 2px 13px; }

/* Responsive */
@media (max-width: 768px) {
  .etx-h1 { font-size: 1.5rem; }
  .etx-bar { padding: 12px 14px; border-radius: 16px; }
  .etx-s-wrap { min-width: 100%; }
  .etx-panel { width: 100vw; }
  .etx-th-hide, .etx-td-hide { display: none; }
  .etx-table-wrap { overflow-x: auto; }
}
`;

const ddSty = {
  background: "rgba(8,16,38,0.98)",
  border: "1px solid rgba(34,211,238,0.15)",
  borderRadius: 16,
  color: "#e8f4ff",
  fontFamily: "'DM Sans', sans-serif",
  boxShadow: "0 20px 60px rgba(0,0,0,0.75)",
  backdropFilter: "blur(20px)",
};

const STATUS_CONFIG = {
  success: { color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.25)", icon: CheckCircle2 },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)", icon: Clock },
  processing: { color: "#3b82f6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.25)", icon: Loader },
  failed: { color: "#fb7185", bg: "rgba(251,113,133,0.12)", border: "rgba(251,113,133,0.25)", icon: XCircle },
  expired: { color: "#5a82a0", bg: "rgba(90,130,160,0.12)", border: "rgba(90,130,160,0.25)", icon: XCircle },
};

const CSV_HEADERS = [
  "transaction_id",
  "merchant_ref",
  "merchant_code",
  "provider_code",
  "type",
  "amount",
  "status",
  "payment_method",
  "created_date",
  "processed_at",
  "error_message"
];

function StatusBadge({ status }) {
  const s = STATUS_CONFIG[(status || "pending").toLowerCase()] || STATUS_CONFIG.pending;
  const Icon = s.icon;
  return (
    <span className="etx-status" style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
      <Icon size={11} />
      {status || "pending"}
    </span>
  );
}

function DetailPanel({ tx, onClose }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  if (!tx) return null;

  const copy = () => { navigator.clipboard.writeText(tx.transaction_id || ""); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const isDeposit = tx.type === "deposit";
  const color = isDeposit ? "#22d3ee" : "#10b981";

  const timeline = [
    { label: t("createdLabel") || "Created", time: tx.created_date, done: true },
    { label: t("processingLabel") || "Processing", time: tx.processed_at, done: ["processing", "success", "failed"].includes(tx.status) },
    { label: tx.status === "failed" ? (t("failedLabel") || "Failed") : (t("completedLabel") || "Completed"), time: tx.completed_at, done: ["success", "failed"].includes(tx.status), failed: tx.status === "failed" },
  ];

  return (
    <>
      <motion.div className="etx-panel-ov" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="etx-panel" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 36 }}>
        <div className="etx-panel-head">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="etx-icon" style={{ background: isDeposit ? "rgba(34,211,238,0.12)" : "rgba(16,185,129,0.12)", border: `1px solid ${isDeposit ? "rgba(34,211,238,0.25)" : "rgba(16,185,129,0.25)"}` }}>
                {isDeposit ? <ArrowDownToLine style={{ color: "#22d3ee" }} /> : <ArrowUpFromLine style={{ color: "#10b981" }} />}
              </div>
              <div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: "1rem", color: "#eaf6ff", textTransform: "capitalize" }}>{tx.type}</div>
                <StatusBadge status={tx.status} />
              </div>
            </div>
            <button className="etx-panel-close" onClick={onClose}><X size={14} /></button>
          </div>
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "11px", color: "#3a5a78", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 6 }}>Amount</div>
            <div className="etx-amount-big" style={{ color }}>฿{tx.amount?.toLocaleString()}</div>
          </div>
        </div>

        <div className="etx-panel-body">
          {/* Details */}
          <div style={{ background: "rgba(34,211,238,0.03)", border: "1px solid rgba(34,211,238,0.08)", borderRadius: 14, padding: "4px 16px", marginBottom: 20 }}>
            {[
              { label: t("merchant") || "Merchant", value: tx.merchant_code || "-" },
              { label: t("provider") || "Provider", value: tx.provider_code || "-" },
              { label: t("method") || "Method", value: tx.payment_method?.replace(/_/g, " ") || "-" },
              { label: t("ref") || "Reference", value: tx.merchant_ref || "-" },
            ].map(({ label, value }) => (
              <div key={label} className="etx-detail-row">
                <span className="etx-detail-lbl">{label}</span>
                <span className="etx-detail-val">{value}</span>
              </div>
            ))}
          </div>

          {/* Transaction ID */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "10px", color: "#3a5a78", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 8 }}>Transaction ID</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(34,211,238,0.04)", border: "1px solid rgba(34,211,238,0.1)", borderRadius: 10, padding: "10px 14px" }}>
              <code style={{ flex: 1, fontFamily: "'JetBrains Mono',monospace", fontSize: "11px", color: "var(--cyan)", wordBreak: "break-all" }}>{tx.transaction_id}</code>
              <button className="etx-copy" onClick={copy}>{copied ? <Check size={13} style={{ color: "#10b981" }} /> : <Copy size={13} />}</button>
            </div>
          </div>

          {/* Timeline */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "10px", color: "#3a5a78", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 14 }}>Timeline</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {timeline.map((step, i) => {
                const c = step.done ? (step.failed ? "#fb7185" : "#10b981") : "#2e4a66";
                const Icon = step.done ? (step.failed ? XCircle : CheckCircle2) : Clock;
                return (
                  <div key={i} className="etx-timeline-item" style={{ paddingBottom: i < 2 ? 4 : 0 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div className="etx-timeline-dot" style={{ background: `${c}18`, border: `1px solid ${c}40` }}>
                        <Icon size={14} style={{ color: c }} />
                      </div>
                      {i < 2 && <div className="etx-timeline-line" style={{ background: step.done ? `${c}30` : "rgba(34,211,238,0.06)" }} />}
                    </div>
                    <div style={{ paddingTop: 5, paddingLeft: 4 }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: step.done ? "#e8f4ff" : "#3a5a78" }}>{step.label}</div>
                      {step.time && step.done && <div style={{ fontSize: "11px", color: "#3a5a78", marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>{format(new Date(step.time), "MMM d, HH:mm:ss")}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {tx.error_message && (
            <div style={{ background: "rgba(251,113,133,0.07)", border: "1px solid rgba(251,113,133,0.2)", borderRadius: 12, padding: "12px 16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#fb7185", marginBottom: 4 }}>Error</div>
              <div style={{ fontSize: "12.5px", color: "#fca5a5" }}>{tx.error_message}</div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function EngineTransactions() {
  const { t } = useLanguage();
  const engineId = auth.getUserData()?.userId;

  // 1. Hierarchy Scoping
  const { data: rawResellers = [], isFetching: resellersFetching } = useQuery({
    queryKey: ["engine-hierarchy-resellers", engineId],
    queryFn: async () => {
      const res = await engineService.getMyResellers(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const { data: rawAllMerchants = [], isFetching: merchantsFetching } = useQuery({
    queryKey: ["engine-hierarchy-merchants-all", engineId],
    queryFn: async () => {
      // Get all merchants the engine has access to (both direct and via resellers)
      const res = await engineService.getMyMerchants(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const filteredMerchants = useMemo(() => {
    if (!engineId) return [];

    // All Merchants discovery
    const resellerIds = new Set(rawResellers.map(r => r.id));

    // Filter into categories based on creation hierarchy
    const merchantsByEngine = rawAllMerchants.filter(m => m.createdByEngineId === engineId);
    const merchantsByResellers = rawAllMerchants.filter(m => resellerIds.has(m.createdByResellerId));

    // Combine and deduplicate
    const combinedMap = new Map();
    [...merchantsByEngine, ...merchantsByResellers].forEach(m => {
      if (m && m.id) combinedMap.set(m.id, m);
    });

    return Array.from(combinedMap.values());
  }, [rawAllMerchants, rawResellers, engineId]);

  const allowedMerchantIds = useMemo(() => filteredMerchants.map(m => m.id), [filteredMerchants]);

  const { data: profile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  const resellerName = profile?.name || "Engine Partner";

  // 2. Filters & State
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selTx, setSelTx] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(0);
  const PG = 10;

  const filtersMapper = useMemo(() => ({
    status: statusFilter !== "all" ? statusFilter : undefined,
    type: typeFilter !== "all" ? typeFilter.toUpperCase() : undefined,
    startDate: dateRange.from ? dateRange.from.toISOString() : undefined,
    endDate: dateRange.to ? dateRange.to.toISOString() : undefined,
  }), [statusFilter, typeFilter, dateRange]);

  const targetMerchantIds = useMemo(() => {
    if (merchantFilter && merchantFilter !== "all") return [merchantFilter];
    return allowedMerchantIds.length > 0 ? allowedMerchantIds : ["__NO_SCOPE__"];
  }, [merchantFilter, allowedMerchantIds]);


  // 3. Main Query
  const { data: combinedData, isLoading, isFetching: transactionsFetching, refetch } = useQuery({
    queryKey: ["engine-combined-transactions", page, PG, search, filtersMapper, targetMerchantIds],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      PG,
      search,
      filtersMapper,
      targetMerchantIds
    ),
    refetchInterval: 5000,
    keepPreviousData: true,
    enabled: !!engineId && (merchantFilter !== "all" || allowedMerchantIds.length > 0),
  });

  const isSyncing = resellersFetching || merchantsFetching || transactionsFetching;

  const rawTransactions = combinedData?.data || [];
  const totalItems = combinedData?.total || 0;
  const totalPages = combinedData?.totalPages || 0;

  const transactions = useMemo(() => {
    return rawTransactions.map(t => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id || t.id,
      type: (t.type || "deposit").toLowerCase(),
      status: (t.status || "pending").toLowerCase(),
      amount: Number(t.amount || 0),
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
    }));
  }, [rawTransactions]);

  const stats = useMemo(() => {
    const successCount = transactions.filter(t => t.status === "success").length;
    const pendingCount = transactions.filter(t => t.status === "pending" || t.status === "processing").length;
    const depCount = transactions.filter(t => t.type === "deposit").length;
    const witCount = transactions.filter(t => t.type === "withdrawal").length;
    return { successCount, pendingCount, depCount, witCount };
  }, [transactions]);

  const hasF = search || statusFilter !== "all" || typeFilter !== "all" || merchantFilter !== "all" || dateRange.from;
  const clear = () => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); setMerchantFilter("all"); setDateRange({ from: null, to: null }); setPage(0); };
  const nums = useMemo(() => { const a = []; for (let i = Math.max(0, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) a.push(i); return a; }, [page, totalPages]);

  const copy = (e, id) => { e.stopPropagation(); navigator.clipboard.writeText(id); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };

  const handleExport = () => {
    exportToCsv(transactions, {
      headers: CSV_HEADERS,
      filename: `engine-transactions-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: (r, k) => {
        if (k === "amount") return r.amount?.toLocaleString();
        if (k === "created_date" || k === "processed_at") return r[k] ? format(new Date(r[k]), "yyyy-MM-dd HH:mm:ss") : "-";
        return r[k];
      }
    });
  };

  return (
    <EngineLayout currentPage="EngineTransactions" resellerName={resellerName}>
      <style>{CSS}</style>
      <div className="etx">
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -16, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 16, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#22d3ee,#0891b2)", boxShadow: "0 10px 30px rgba(34,211,238,0.35)", position: "relative", overflow: "hidden" }} className={isSyncing ? "animate-pulse" : ""}>
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg,rgba(255,255,255,0.15),transparent)" }} />
                  {isSyncing ? <Loader className="animate-spin text-white" size={22} /> : <Activity size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />}
                </div>
                <div>
                  <div className="etx-h1"><h1 className="text-3xl sm:text-3xl font-bold text-slate-100 mb-1">{t("Transactions")}</h1></div>
                  <div className="etx-sub">{totalItems} {t("transactionsFound") || "transactions found"}</div>
                </div>
              </div>
              <button className="etx-export" onClick={handleExport}>
                <Download />{t("exportCsv") || "Export CSV"}
              </button>
            </div>
            <div className="etx-summary">
              {[
                { dot: "#10b981", label: `${stats.successCount} Success` },
                { dot: "#f59e0b", label: `${stats.pendingCount} Pending` },
                { dot: "#22d3ee", label: `${stats.depCount} Deposits` },
                { dot: "#a78bfa", label: `${stats.witCount} Withdrawals` },
                ...(isSyncing ? [{ dot: "#22d3ee", label: "Syncing..." }] : []),
              ].map(({ dot, label }) => (
                <motion.div key={label} className="etx-badge" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 400, damping: 22 }}>
                  <div className="etx-badge-dot" style={{ background: dot, boxShadow: `0 0 8px ${dot}` }} />
                  {label}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div className="etx-bar" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <ListFilter style={{ width: 16, height: 16, color: "#3a5a78", flexShrink: 0 }} />
            <div className="etx-s-wrap">
              <Search className="etx-s-ico" />
              <input className="etx-s-in" placeholder={t("searchHeaderPlaceholder") || "Search transactions..."} value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
            </div>

            <Select value={merchantFilter} onValueChange={v => { setMerchantFilter(v); setPage(0); }}>
              <SelectTrigger className="etx-sel" style={{ width: 142 }}><SelectValue placeholder={t("merchant") || "Merchant"} /></SelectTrigger>
              <SelectContent style={ddSty}>
                <SelectItem value="all">{t("allMerchants") || "All Merchants"}</SelectItem>
                {filteredMerchants.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="etx-sel" style={{ width: 130 }}><SelectValue placeholder={t("status") || "Status"} /></SelectTrigger>
              <SelectContent style={ddSty}>
                <SelectItem value="all">{t("allStatus") || "All Status"}</SelectItem>
                <SelectItem value="pending">{t("pending") || "Pending"}</SelectItem>
                <SelectItem value="processing">{t("processing") || "Processing"}</SelectItem>
                <SelectItem value="success">{t("success") || "Success"}</SelectItem>
                <SelectItem value="failed">{t("failed") || "Failed"}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="etx-sel" style={{ width: 130 }}><SelectValue placeholder={t("type") || "Type"} /></SelectTrigger>
              <SelectContent style={ddSty}>
                <SelectItem value="all">{t("allTypes") || "All Types"}</SelectItem>
                <SelectItem value="deposit">{t("deposit") || "Deposit"}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawal") || "Withdrawal"}</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <button className="etx-date">
                  <Calendar />
                  {dateRange.from ? dateRange.to ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}` : format(dateRange.from, "MMM d") : t("dateRange") || "Date Range"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" style={{ background: "rgba(8,16,38,0.98)", border: "1px solid rgba(34,211,238,0.15)", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,0.75)", backdropFilter: "blur(24px)" }}>
                <Cal mode="range" selected={dateRange} onSelect={r => { setDateRange(r || { from: null, to: null }); setPage(0); }} numberOfMonths={2} />
              </PopoverContent>
            </Popover>

            <AnimatePresence>
              {hasF && (
                <motion.button className="etx-clr" onClick={clear} initial={{ opacity: 0, scale: 0.82, x: 10 }} animate={{ opacity: 1, scale: 1, x: 0 }} exit={{ opacity: 0, scale: 0.82, x: 10 }} transition={{ duration: 0.22 }}>
                  <X />{t("clearFilters") || "Clear"}
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Table */}
          <motion.div className="etx-table-wrap" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.45, ease: [0.22, 1, 0.36, 1] }} style={{ opacity: isSyncing && !isLoading ? 0.6 : 1, transition: "opacity 0.3s ease" }}>
            <table className="etx-table">
              <thead>
                <tr>
                  <th className="etx-th">{t("transaction") || "Transaction"}</th>
                  <th className="etx-th etx-th-hide">{t("merchant") || "Merchant"}</th>
                  <th className="etx-th">{t("type") || "Type"}</th>
                  <th className="etx-th">{t("amount") || "Amount"}</th>
                  <th className="etx-th">{t("status") || "Status"}</th>
                  <th className="etx-th etx-th-hide">{t("date") || "Date"}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="etx-empty"><Loader size={20} style={{ color: "#22d3ee", animation: "spin 1s linear infinite", display: "inline-block" }} /></td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={6} className="etx-empty">{t("noTransactionsFound") || "No transactions found"}</td></tr>
                ) : transactions.map((tx, idx) => {
                  const isDeposit = tx.type === "deposit";
                  return (
                    <motion.tr key={tx.id || idx} className="etx-tr" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }} onClick={() => setSelTx(tx)}>
                      <td className="etx-td">
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="etx-icon" style={{ background: isDeposit ? "rgba(34,211,238,0.1)" : "rgba(16,185,129,0.1)", border: `1px solid ${isDeposit ? "rgba(34,211,238,0.2)" : "rgba(16,185,129,0.2)"}` }}>
                            {isDeposit ? <ArrowDownToLine style={{ color: "#22d3ee" }} /> : <ArrowUpFromLine style={{ color: "#10b981" }} />}
                          </div>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span className="etx-mono" style={{ color: "#e8f4ff" }}>{tx.transaction_id?.slice(0, 12)}...</span>
                              <button className="etx-copy" onClick={e => copy(e, tx.transaction_id)}>
                                {copiedId === tx.transaction_id ? <Check size={11} style={{ color: "#10b981" }} /> : <Copy size={11} />}
                              </button>
                            </div>
                            <div style={{ fontSize: "11px", color: "#3a5a78", marginTop: 1 }}>{tx.provider_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="etx-td etx-td-hide">
                        <span style={{ fontSize: "13px", color: "#7aaec8" }}>{tx.merchant_code || "-"}</span>
                      </td>
                      <td className="etx-td">
                        <span style={{ fontSize: "13px", fontWeight: 600, color: isDeposit ? "#22d3ee" : "#10b981", textTransform: "capitalize" }}>{isDeposit ? (t("deposit") || "Deposit") : (t("withdrawal") || "Withdrawal")}</span>
                      </td>
                      <td className="etx-td">
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: "#e8f4ff", fontSize: "13px" }}>฿{tx.amount?.toLocaleString()}</span>
                      </td>
                      <td className="etx-td"><StatusBadge status={tx.status} /></td>
                      <td className="etx-td etx-td-hide">
                        <span style={{ fontSize: "12px", color: "#3a5a78", fontFamily: "'JetBrains Mono',monospace" }}>{tx.created_date ? format(new Date(tx.created_date), "MMM d, HH:mm") : "-"}</span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div className="etx-pag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}>
              <div className="etx-pag-info">{t("showing") || "Showing"} {page * PG + 1}–{Math.min((page + 1) * PG, totalItems)} {t("of") || "of"} {totalItems}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button className="etx-arr" onClick={() => setPage(p => p - 1)} disabled={page === 0}><ChevronLeft /></button>
                {nums.map(n => <motion.button key={n} className={`etx-pg${n === page ? " on" : ""}`} onClick={() => setPage(n)} whileTap={{ scale: 0.92 }}>{n + 1}</motion.button>)}
                <button className="etx-arr" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}><ChevronRight /></button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Detail Panel */}
      <AnimatePresence>
        {selTx && <DetailPanel tx={selTx} onClose={() => setSelTx(null)} />}
      </AnimatePresence>
    </EngineLayout>
  );
}